import { ChangeDetectorRef, Component } from "@angular/core";
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from "@angular/forms";
import { DxButtonModule } from "devextreme-angular/ui/button";
import { DxLoadPanelModule } from "devextreme-angular/ui/load-panel";
import { DxTextBoxModule } from "devextreme-angular/ui/text-box";
import { AccountService, AuthResponse } from "../../services/account.service";
import { HttpErrorResponse } from "@angular/common/http";
import { finalize } from "rxjs";
import { HomePageComponent } from "../home-page/home-page.component";
import { Router } from "@angular/router";

@Component({
  selector: "app-login-page",
  imports: [ReactiveFormsModule, DxTextBoxModule, DxButtonModule, DxLoadPanelModule],
  templateUrl: "./login-page.component.html",
  styleUrls: ["./login-page.component.scss"],
})
export class LoginPageComponent {
  public static readonly Path = "login";

  public statusKind: "error" | "success" | "info" = "info";
  public statusMessage?: string;
  public showSpinner = false;

  public readonly loginForm = new FormGroup({
    email: new FormControl("", {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl("", {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(6)],
    }),
  });

  public constructor(
    private accountsService: AccountService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
  ) { }

  public get emailControl() {
    return this.loginForm.controls.email;
  }

  public get passwordControl() {
    return this.loginForm.controls.password;
  }

  public get emailInvalid() {
    return this.emailControl.invalid && (this.emailControl.dirty || this.emailControl.touched);
  }

  public get passwordInvalid() {
    return this.passwordControl.invalid && (this.passwordControl.dirty || this.passwordControl.touched);
  }

  public async performLogin() {
    this.statusMessage = undefined;
    this.loginForm.markAllAsTouched();

    if (this.loginForm.invalid) {
      this.statusKind = "error";
      this.statusMessage = "Please enter a valid email and password.";
      this.showSpinner = false;
      return;
    }

    this.showSpinner = true;
    await new Promise((resolve) => setTimeout(resolve, 1000)); // delay

    this.accountsService.login(this.emailControl.value, this.passwordControl.value)
      .pipe(
        finalize(() => {
          this.showSpinner = false;
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe({
        next: (authResponse: AuthResponse) => {
          this.statusKind = "success";
          this.statusMessage = `Logged in as ${authResponse.email}`;
          this.homeAfterDelay();
        },
        error: (e: HttpErrorResponse) => {
          const message = typeof e.error === "string"
            ? e.error
            : e.error?.message;
          this.statusKind = "error";
          this.statusMessage = message || "An error occurred during login. Please try again.";
        },
      });
  }

  public cancel() {
    this.loginForm.reset({
      email: "",
      password: "",
    });
    this.statusMessage = undefined;
  }

  public async registerNewAccount() {
    this.statusMessage = undefined;
    this.loginForm.markAllAsTouched();

    if (this.loginForm.invalid) {
      this.statusKind = "error";
      this.statusMessage = "Please enter a valid email and password.";
      this.showSpinner = false;
      return;
    }

    this.showSpinner = true;
    await new Promise((resolve) => setTimeout(resolve, 1000)); // delay
    this.accountsService.registerNewAccount(this.emailControl.value, this.passwordControl.value)
      .pipe(
        finalize(() => {
          this.showSpinner = false;
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe({
        next: (authResponse: AuthResponse) => {
          this.statusKind = "success";
          this.statusMessage = `Registered as ${authResponse.email}`;
          this.homeAfterDelay();
        },
        error: (e: HttpErrorResponse) => {
          const message = typeof e.error === "string"
            ? e.error
            : e.error?.message;
          this.statusKind = "error";
          this.statusMessage = message || "An error occurred during registration. Please try again.";
        },
      });
  }

  private async homeAfterDelay() {
    await new Promise((resolve) => setTimeout(resolve, 500));
    await this.router.navigateByUrl(HomePageComponent.Path);
  }
}
